import type { PropsWithChildren } from "react";
import { SidebarTrigger, useSidebar } from "@corpuscomplete/web-ui/components/sidebar";

/**
 * Root page flex container used by authenticated route pages.
 * Contract: own viewport-height layout primitives only; business state
 * comes from route loaders and @corpuscomplete/core-ui providers.
 */
export function Page({ children }: PropsWithChildren) {
  return <div className="flex h-full flex-row overflow-hidden">{children}</div>;
}

/**
 * Vertical content column that fills remaining page width.
 * Contract: children are expected to manage their own data providers/state.
 */
export function PageBody({ children }: PropsWithChildren) {
  return <div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>;
}

/**
 * Shared page header row with sidebar toggle behavior.
 * Contract: reads sidebar open state from web-ui provider context only.
 */
export function PageHeader({ children }: PropsWithChildren) {
  const { open } = useSidebar();
  return (
    <div className="flex h-10 shrink-0 items-center gap-2 border-b">
      <div className="flex w-full items-center gap-2 px-4 py-2 font-medium">
        {!open && <SidebarTrigger />}
        {children}
      </div>
    </div>
  );
}

/**
 * Scrollable main content container for route page bodies.
 * Contract: callers pass layout classes; scrolling ownership stays local.
 */
export function PageContent({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={`flex flex-1 flex-col overflow-y-auto ${className ?? ""}`}>{children}</div>
  );
}

/**
 * Fixed width used by PageSidePanel open/close transitions.
 */
export const PAGE_SIDE_PANEL_WIDTH = 400;

/**
 * Optional right-side panel with slide transition.
 * Contract: open state is controlled by caller; this component is presentational.
 */
export function PageSidePanel({
  open,
  setOpen: _setOpen,
  children,
}: PropsWithChildren<{
  open: boolean;
  setOpen: (open: boolean) => void;
}>) {
  return (
    <div
      className="flex h-full flex-shrink-0 flex-col overflow-y-auto border-l bg-background transition-[transform,margin] duration-150 ease-in-out"
      style={{
        width: PAGE_SIDE_PANEL_WIDTH,
        marginRight: open ? 0 : -PAGE_SIDE_PANEL_WIDTH,
        transform: open ? "translateX(0)" : `translateX(${PAGE_SIDE_PANEL_WIDTH}px)`,
      }}
    >
      {children}
    </div>
  );
}
